import { groupBy, sum } from '../../../helpers/array'
import { ItemWorkareaData, ViewItem } from '../itemModel'

export type ItemCalculationMode = 'estimated' | 'actual'

export type CalculationTotals = {
  totalCost: number
  totalTax: number
  totalOverhead: number
  totalBreakeven: number
  totalPrice: number
  totalProfit: number
}

export type WorkareaCalculationGroup = {
  workareaId: number
  workareaName: string
  isOnHold: boolean
  isApproved: boolean
  estimatedOccurrences: number
  items: ViewItem[]
  subtotals: CalculationTotals
  subtotalsWithOccurrences: CalculationTotals
}

export const formatDecimalFactor = (value: number): string => {
  return (Math.round(value * 10000) / 10000).toString()
}

export const taxAmount = (item: ViewItem): number => item.costPlusTax - item.cost

export const overheadAmount = (item: ViewItem): number => item.breakeven - item.costPlusTax

export const unitProfit = (item: ViewItem): number => item.unitPrice - item.breakeven


export const emptyTotals = (): CalculationTotals => ({
  totalCost: 0,
  totalTax: 0,
  totalOverhead: 0,
  totalBreakeven: 0,
  totalPrice: 0,
  totalProfit: 0,
})

export const sumCalculationTotals = (items: ViewItem[]): CalculationTotals => {
  const visible = items.filter(x => !x.isDeleted)
  return {
    totalCost: sum(visible, x => x.cost * x.quantity),
    totalTax: sum(visible, x => taxAmount(x) * x.quantity),
    totalOverhead: sum(visible, x => overheadAmount(x) * x.quantity),
    totalBreakeven: sum(visible, x => x.totalBreakeven),
    totalPrice: sum(visible, x => x.totalPrice),
    totalProfit: sum(visible, x => x.totalPrice - x.totalBreakeven),
  }
}

export const multiplyTotals = (totals: CalculationTotals, factor: number): CalculationTotals => ({
  totalCost: totals.totalCost * factor,
  totalTax: totals.totalTax * factor,
  totalOverhead: totals.totalOverhead * factor,
  totalBreakeven: totals.totalBreakeven * factor,
  totalPrice: totals.totalPrice * factor,
  totalProfit: totals.totalProfit * factor,
})

export const addTotals = (a: CalculationTotals, b: CalculationTotals): CalculationTotals => ({
  totalCost: a.totalCost + b.totalCost,
  totalTax: a.totalTax + b.totalTax,
  totalOverhead: a.totalOverhead + b.totalOverhead,
  totalBreakeven: a.totalBreakeven + b.totalBreakeven,
  totalPrice: a.totalPrice + b.totalPrice,
  totalProfit: a.totalProfit + b.totalProfit,
})

export const groupCalculationsByWorkarea = (
  items: ViewItem[],
  workareas: ItemWorkareaData[],
  mode: ItemCalculationMode,
  groupSortIndexById: Record<string, number> = {}
): WorkareaCalculationGroup[] => {
  const visibleItems = items.filter(x => !x.isEmptyRow && !x.isDeleted)
  const grouped = groupBy(visibleItems, x => x.workareaId)

  const groupSortIndex = (workarea: ItemWorkareaData) => groupSortIndexById[workarea.workareaGroupId ?? ''] ?? 0

  return [...workareas]
    .sort((a, b) => groupSortIndex(a) - groupSortIndex(b) || a.sortIndex - b.sortIndex)
    .filter(w => (grouped[w.id]?.length ?? 0) > 0)
    .map(w => {
      const workareaItems = grouped[w.id]
      const subtotals = sumCalculationTotals(workareaItems)
      const estimatedOccurrences = mode === 'estimated' ? w.estimatedOccurrences ?? 1 : 1
      return {
        workareaId: w.id,
        workareaName: w.name,
        isOnHold: w.isOnHold,
        isApproved: w.isApproved,
        estimatedOccurrences,
        items: workareaItems,
        subtotals,
        subtotalsWithOccurrences: multiplyTotals(subtotals, estimatedOccurrences),
      }
    })
}

export const sumGrandTotals = (groups: WorkareaCalculationGroup[]): CalculationTotals => {
  return groups
    .filter(group => !group.isOnHold)
    .reduce((acc, group) => addTotals(acc, group.subtotalsWithOccurrences), emptyTotals())
}
